/**
 * Editor de receta (produce / fracciona) embebido en ficha de producto o stack.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { formatUnitLabel, normalizeUnitOfMeasure } from '@shared/quantity';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { NumericTextInput } from '../../shared/ui/NumericTextInput';
import { showError, showSuccess } from '../../shared/ui/appToast';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { getCatalogRamSnapshotCache } from '../infra/catalogRamSnapshotCache';
import {
  getRecipeForProduct,
  upsertRecipe,
  type RecipeKind,
  type RecipeLineDto,
} from '../infra/recipesRemoteRepo';
import { invalidateRecipeProductIdsCache } from '../infra/recipeProductIdsRepo';
import { matchesSearchText } from '../../shared/format/searchText';
import { parseDecimalInput } from '../../shared/numeric/public-api';
import { OperationalFilterSegmentedRow } from '../../shared/filters/OperationalFilterPrimitives';

type LineDraft = {
  key: string;
  productId: string;
  productName: string;
  unitLabel: string;
  qty: string;
  isShrinkage: boolean;
};

function newKey() {
  return `l-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function uomLabel(raw: string | null | undefined): string {
  return formatUnitLabel(normalizeUnitOfMeasure(raw));
}

type Props = {
  productId: string;
  productName: string;
  /** Receta activa en el panel (producir vs fraccionar). */
  kind: RecipeKind;
  onKindChange?: (kind: RecipeKind) => void;
  canEdit: boolean;
  onSaved?: () => void;
  onProduce?: () => void;
  onDisassembleRun?: () => void;
  /** Muestra selector Producir / Fraccionar arriba (ficha). */
  showKindSwitch?: boolean;
};

export function CatalogProductRecipePanel({
  productId,
  productName,
  kind,
  onKindChange,
  canEdit,
  onSaved,
  onProduce,
  onDisassembleRun,
  showKindSwitch = false,
}: Props) {
  const hasRecipes = useHasBusinessCapability('recipes');
  const currentBranchId = useSessionStore((s) => s.currentBranchId);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [yieldQty, setYieldQty] = useState('1');
  const [lines, setLines] = useState<LineDraft[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');

  const finishedUnit = useMemo(() => {
    const products = getCatalogRamSnapshotCache()?.products ?? [];
    const row = products.find((p) => String(p.id) === productId);
    return uomLabel(row?.unit_of_measure);
  }, [productId]);

  const howItWorks =
    kind === 'disassemble'
      ? 'Cuánto madre usás y en qué se parte. Cada corte en su unidad.'
      : `Insumos en su unidad. Rinde = cuánto ${productName} sale.`;

  const load = useCallback(async () => {
    if (!productId || !hasRecipes) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const recipe = await getRecipeForProduct(productId, kind);
      const products = getCatalogRamSnapshotCache()?.products ?? [];
      const uomById = new Map(
        products.map((p) => [String(p.id), uomLabel(p.unit_of_measure)] as const),
      );
      if (recipe) {
        setYieldQty(String(recipe.yieldQty || 1).replace('.', ','));
        setLines(
          recipe.lines.map((l: RecipeLineDto) => ({
            key: l.id ?? newKey(),
            productId: l.productId,
            productName: l.productName ?? l.productId,
            unitLabel: uomById.get(l.productId) ?? 'u',
            qty: String(l.qty).replace('.', ','),
            isShrinkage: Boolean(l.isShrinkage),
          })),
        );
      } else {
        setYieldQty('1');
        setLines([]);
      }
    } catch (error) {
      presentUnexpectedFailure({
        error,
        module: 'catalog',
        op: 'recipes.panel.load',
        userMessage: 'No pudimos cargar la receta. Reintentá.',
      });
    } finally {
      setLoading(false);
    }
  }, [productId, kind, hasRecipes]);

  useEffect(() => {
    void load();
  }, [load]);

  const catalogProducts = useMemo(() => {
    const products = getCatalogRamSnapshotCache()?.products ?? [];
    return products
      .filter(
        (p) =>
          String(p.id) !== productId &&
          p.is_active !== false &&
          p.item_type !== 'service' &&
          p.is_misc !== true,
      )
      .map((p) => ({
        id: String(p.id),
        name: String(p.name ?? p.id),
        unitLabel: uomLabel(p.unit_of_measure),
      }));
  }, [productId]);

  const filteredPicker = useMemo(() => {
    const q = pickerQuery.trim();
    if (!q) return catalogProducts.slice(0, 40);
    return catalogProducts.filter((p) => matchesSearchText(p.name, q)).slice(0, 40);
  }, [catalogProducts, pickerQuery]);

  const addLine = (product: { id: string; name: string; unitLabel: string }) => {
    if (lines.some((l) => l.productId === product.id)) {
      showError('Ya está', 'Ese producto ya está en la receta.');
      return;
    }
    setLines((prev) => [
      ...prev,
      {
        key: newKey(),
        productId: product.id,
        productName: product.name,
        unitLabel: product.unitLabel,
        qty: '1',
        isShrinkage: false,
      },
    ]);
    setPickerOpen(false);
    setPickerQuery('');
  };

  const save = async () => {
    if (!canEdit) return;
    const yieldNum = parseDecimalInput(yieldQty);
    if (yieldNum == null || yieldNum <= 0) {
      showError('Rinde', 'Poné un número mayor a 0.');
      return;
    }
    const parsed = lines.map((l) => ({
      productId: l.productId,
      qty: parseDecimalInput(l.qty),
      isShrinkage: l.isShrinkage,
    }));
    if (parsed.length === 0) {
      showError('Insumos', 'Agregá al menos un renglón.');
      return;
    }
    if (parsed.some((l) => l.qty == null || l.qty <= 0)) {
      showError('Cantidades', 'Todas las cantidades tienen que ser mayores a 0.');
      return;
    }
    setSaving(true);
    try {
      await upsertRecipe({
        productId,
        kind,
        yieldQty: yieldNum,
        branchId: currentBranchId,
        lines: parsed.map((l, i) => ({
          productId: l.productId,
          qty: l.qty as number,
          sortOrder: i,
          isShrinkage: kind === 'disassemble' ? l.isShrinkage : false,
        })),
      });
      showSuccess(
        kind === 'disassemble' ? 'Fraccionamiento guardado' : 'Receta guardada',
        'Listo.',
      );
      invalidateRecipeProductIdsCache();
      await load();
      onSaved?.();
    } catch (error) {
      presentUnexpectedFailure({
        error,
        module: 'catalog',
        op: 'recipes.panel.upsert',
        userMessage: 'No pudimos guardar la receta. Reintentá.',
      });
    } finally {
      setSaving(false);
    }
  };

  if (!hasRecipes) {
    return (
      <Text style={styles.muted}>
        Recetas apagadas. Prendelas en Funciones del negocio.
      </Text>
    );
  }

  if (loading) {
    return (
      <View style={styles.loadingWrap}>
        <ActivityIndicator color={ticketColors.ink} />
      </View>
    );
  }

  return (
    <View style={styles.wrap} testID="catalog.recipe-panel">
      {showKindSwitch && onKindChange ? (
        <OperationalFilterSegmentedRow<RecipeKind>
          ticketTone
          options={[
            { value: 'assemble', label: 'Producir' },
            { value: 'disassemble', label: 'Fraccionar' },
          ]}
          value={kind}
          onChange={onKindChange}
          testIDPrefix="catalog.recipe.kind"
        />
      ) : null}

      <Text style={styles.hint}>{howItWorks}</Text>

      <Text style={styles.fieldLabel}>
        {kind === 'disassemble' ? 'Cantidad madre' : 'Rinde'}
      </Text>
      <Text style={styles.fieldHint}>
        {kind === 'disassemble'
          ? `Unidades madre por fraccionamiento (${finishedUnit}).`
          : `Cuánto sale con los insumos de abajo (${finishedUnit}).`}
      </Text>
      <View style={styles.qtyRow}>
        <NumericTextInput
          kind="signedDecimal"
          style={styles.qtyInput}
          value={yieldQty}
          onChangeText={setYieldQty}
          placeholder="1"
          placeholderTextColor={ticketColors.ink3}
          editable={canEdit}
          testID="catalog.recipe.yieldInput"
        />
        <Text style={styles.qtyUnit}>{finishedUnit}</Text>
      </View>

      <View style={styles.sectionHead}>
        <Text style={styles.sectionTitle}>
          {kind === 'disassemble' ? 'Cortes' : 'Insumos'}
        </Text>
        {canEdit ? (
          <TouchableOpacity
            style={styles.addBtn}
            onPress={() => setPickerOpen((v) => !v)}
            testID="catalog.recipe.addLine"
          >
            <Text style={styles.addBtnText}>{pickerOpen ? 'Cerrar' : 'Agregar'}</Text>
          </TouchableOpacity>
        ) : null}
      </View>

      {pickerOpen && canEdit ? (
        <View style={styles.picker}>
          <TextInput
            style={styles.searchInput}
            value={pickerQuery}
            onChangeText={setPickerQuery}
            placeholder="Buscar producto…"
            placeholderTextColor={ticketColors.ink3}
            testID="catalog.recipe.pickerSearch"
          />
          {filteredPicker.map((item) => (
            <Pressable
              key={item.id}
              style={styles.pickerRow}
              onPress={() => addLine(item)}
            >
              <Text style={styles.pickerName}>{item.name}</Text>
              <Text style={styles.pickerUnit}>{item.unitLabel}</Text>
            </Pressable>
          ))}
        </View>
      ) : null}

      {lines.length === 0 ? (
        <Text style={styles.emptyHint}>Sin renglones. Agregá insumos o cortes.</Text>
      ) : (
        <View style={styles.linesStack}>
          {lines.map((item, index) => (
            <View key={item.key} style={styles.lineCard}>
              <Text style={styles.lineName}>{item.productName}</Text>
              <View style={styles.lineControls}>
                <View style={styles.qtyRow}>
                  <NumericTextInput
                    kind="signedDecimal"
                    style={styles.qtyInput}
                    value={item.qty}
                    editable={canEdit}
                    onChangeText={(t) =>
                      setLines((prev) =>
                        prev.map((l, i) => (i === index ? { ...l, qty: t } : l)),
                      )
                    }
                  />
                  <Text style={styles.qtyUnit}>{item.unitLabel}</Text>
                </View>
                {kind === 'disassemble' && canEdit ? (
                  <View style={styles.shrinkRow}>
                    <Text style={styles.shrinkLabel}>Merma</Text>
                    <Switch
                      value={item.isShrinkage}
                      onValueChange={(v) =>
                        setLines((prev) =>
                          prev.map((l, i) => (i === index ? { ...l, isShrinkage: v } : l)),
                        )
                      }
                    />
                  </View>
                ) : null}
                {canEdit ? (
                  <TouchableOpacity onPress={() => setLines((prev) => prev.filter((_, i) => i !== index))}>
                    <Text style={styles.removeText}>Quitar</Text>
                  </TouchableOpacity>
                ) : null}
              </View>
            </View>
          ))}
        </View>
      )}

      {canEdit ? (
        <TouchableOpacity
          style={[styles.saveBtn, saving && styles.saveBtnDisabled]}
          onPress={() => void save()}
          disabled={saving}
          testID="catalog.recipe.save"
        >
          <Text style={styles.saveBtnText}>{saving ? 'Guardando…' : 'Guardar receta'}</Text>
        </TouchableOpacity>
      ) : null}

      {kind === 'assemble' && onProduce ? (
        <TouchableOpacity style={styles.secondaryBtn} onPress={onProduce} testID="catalog.recipe.produce">
          <Text style={styles.secondaryBtnText}>Producir lote</Text>
        </TouchableOpacity>
      ) : null}
      {kind === 'disassemble' && onDisassembleRun ? (
        <TouchableOpacity
          style={styles.secondaryBtn}
          onPress={onDisassembleRun}
          testID="catalog.recipe.disassembleRun"
        >
          <Text style={styles.secondaryBtnText}>Fraccionar stock</Text>
        </TouchableOpacity>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { gap: spacing.sm },
  loadingWrap: { paddingVertical: spacing.xl, alignItems: 'center' },
  muted: {
    fontFamily: ticketFonts.sans,
    fontSize: 13,
    color: ticketColors.ink3,
  },
  hint: {
    fontFamily: ticketFonts.sans,
    fontSize: 12,
    lineHeight: 16,
    color: ticketColors.ink3,
  },
  fieldLabel: {
    fontFamily: ticketFonts.sans,
    fontWeight: '700',
    fontSize: 13,
    color: ticketColors.ink,
    marginTop: spacing.sm,
  },
  fieldHint: {
    fontFamily: ticketFonts.sans,
    fontSize: 12,
    color: ticketColors.ink3,
    marginBottom: spacing.xs,
  },
  sectionHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: spacing.md,
  },
  sectionTitle: {
    fontFamily: ticketFonts.sans,
    fontWeight: '700',
    fontSize: 15,
    color: ticketColors.ink,
  },
  qtyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    alignSelf: 'flex-start',
  },
  qtyInput: {
    width: 112,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 8,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    color: ticketColors.ink,
    backgroundColor: ticketColors.tile,
    fontFamily: ticketFonts.sans,
    fontSize: 16,
  },
  qtyUnit: {
    fontFamily: ticketFonts.sans,
    fontWeight: '700',
    fontSize: 14,
    color: ticketColors.ink2,
  },
  addBtn: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
  },
  addBtnText: {
    fontFamily: ticketFonts.sans,
    fontWeight: '700',
    fontSize: 12,
    color: ticketColors.ink,
  },
  picker: {
    gap: spacing.xs,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    padding: spacing.sm,
    backgroundColor: ticketColors.tile,
  },
  searchInput: {
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 8,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  pickerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
  },
  pickerName: { flex: 1, fontFamily: ticketFonts.sans, fontSize: 14, color: ticketColors.ink },
  pickerUnit: { fontFamily: ticketFonts.sans, fontSize: 12, color: ticketColors.ink3 },
  emptyHint: { fontFamily: ticketFonts.sans, fontSize: 13, color: ticketColors.ink3 },
  linesStack: { gap: spacing.sm },
  lineCard: {
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    padding: spacing.md,
    gap: spacing.sm,
    backgroundColor: ticketColors.tile,
  },
  lineName: { fontFamily: ticketFonts.sans, fontWeight: '700', fontSize: 14, color: ticketColors.ink },
  lineControls: { flexDirection: 'row', alignItems: 'center', gap: spacing.md, flexWrap: 'wrap' },
  shrinkRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  shrinkLabel: { fontFamily: ticketFonts.sans, fontSize: 12, color: ticketColors.ink2 },
  removeText: { fontFamily: ticketFonts.sans, fontWeight: '700', fontSize: 12, color: ticketColors.bad },
  saveBtn: {
    marginTop: spacing.md,
    backgroundColor: ticketColors.gold,
    borderRadius: 10,
    paddingVertical: spacing.md,
    alignItems: 'center',
  },
  saveBtnDisabled: { opacity: 0.6 },
  saveBtnText: {
    fontFamily: ticketFonts.sans,
    fontWeight: '700',
    fontSize: 15,
    color: ticketColors.goldInk,
  },
  secondaryBtn: {
    marginTop: spacing.sm,
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
  },
  secondaryBtnText: {
    fontFamily: ticketFonts.sans,
    fontWeight: '700',
    fontSize: 14,
    color: ticketColors.ink,
  },
});
